import { centsToFormattedDollars } from '@fruitful/fruitils';
import { MoneyMapRuleType } from '@fruitful/types';
import React from 'react';
import { BodySmallRegular, View, XStack, YStack } from 'smoothie';

import {
    type IncomeSplitMonthlyCategoryProgress,
    type IncomeSplitProgress,
} from '../types/IncomeSplitOverviewTypes';
import { IncomeSplitOverviewFruitfulLogo } from './IncomeSplitOverviewFruitfulLogo';
import { IncomeSplitProgressDonut } from './IncomeSplitProgressDonut';

const CategoryOverview = ({
    moneyMapRuleType,
    progress,
    previousProgress,
    label,
    thisExecutionCents,
}: {
    moneyMapRuleType: MoneyMapRuleType;
    progress: IncomeSplitMonthlyCategoryProgress;
    previousProgress?: IncomeSplitMonthlyCategoryProgress;
    label: string;
    thisExecutionCents?: number;
}) => {
    const { cents, percentage } = progress;
    const v = thisExecutionCents ?? cents;
    const amountLabel =
        v >= 0 ? `+${centsToFormattedDollars(v)}` : centsToFormattedDollars(v);

    return (
        <YStack
            gap={8}
            alignItems="center"
            justifyContent="space-between"
            userSelect="none"
        >
            <View position="relative" width={78} height={78}>
                <IncomeSplitProgressDonut
                    percentage={percentage}
                    previousProgress={previousProgress}
                    moneyMapRuleType={moneyMapRuleType}
                />
                <View
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    alignItems="center"
                    justifyContent="center"
                >
                    <IncomeSplitOverviewFruitfulLogo
                        moneyMapRuleType={moneyMapRuleType}
                    />
                </View>
            </View>
            <YStack gap={4} alignItems="center" justifyContent="space-between">
                <BodySmallRegular>{label}</BodySmallRegular>
                <BodySmallRegular color="$gray1000">
                    {amountLabel}
                </BodySmallRegular>
            </YStack>
        </YStack>
    );
};

export const IncomeSplitProgressOverview = ({
    progress,
    previousProgress,
    thisExecutionProgress,
}: {
    progress: IncomeSplitProgress;
    previousProgress?: IncomeSplitProgress;
    thisExecutionProgress?: IncomeSplitProgress;
}) => {
    return (
        <XStack
            alignItems="center"
            justifyContent="space-around"
            width="100%"
            paddingHorizontal={16}
        >
            <CategoryOverview
                moneyMapRuleType={MoneyMapRuleType.BILLS}
                label="Bills"
                progress={progress.bills}
                previousProgress={previousProgress?.bills}
                thisExecutionCents={thisExecutionProgress?.bills.cents}
            />
            <CategoryOverview
                moneyMapRuleType={MoneyMapRuleType.SPEND}
                label="Spend"
                progress={progress.spend}
                previousProgress={previousProgress?.spend}
                thisExecutionCents={thisExecutionProgress?.spend.cents}
            />
            <CategoryOverview
                moneyMapRuleType={MoneyMapRuleType.GOAL}
                label="Goals"
                progress={progress.goals}
                previousProgress={previousProgress?.goals}
                thisExecutionCents={thisExecutionProgress?.goals.cents}
            />
        </XStack>
    );
};
